import { useEffect, useMemo, useState } from "react";
import { Button, Icon, Input, Modal, Select } from "@legal-500/legal500-design-system";
import type { SelectOption } from "@legal-500/legal500-design-system";
import type { LegacyPublication } from "@/api/legacy-publications.service";
import { submissionsService, type TaxonomyMode } from "@/api/submissions.service";
import { newAxisEditionOptions } from "@/api/surveyTaxonomy.service";
import { TaxonomyToggle } from "@/components/TaxonomyToggle";
import { usePublications } from "@/hooks/usePublications";
import { useNewAxisEditions } from "@/hooks/useNewAxisEditions";
import { useTaxonomyMode } from "@/hooks/useTaxonomyMode";
import {
  optOuts,
  type CreateOptOutInput,
  type OptOut,
  type OptOutScope,
  type OptOutSkipReason,
} from "@/api/optOuts.service";
import {
  buildPublicationYearOptions,
  extractPublicationYear,
  formatLegacyPublicationOption,
  isStoredEditionUuid,
  isStoredLegacyPublicationId,
  lowercaseEditionOptions,
} from "./optOutPublications.utils";
import styles from "./OptOuts.module.css";

type AddOptOutModalProps = {
  onClose: () => void;
  /**
   * When provided, the modal runs in edit mode: the form is prefilled from this
   * entry and submitting calls the update endpoint instead of create.
   */
  entry?: OptOut;
  /**
   * Called after a successful submit. `updated` is `true` in edit mode.
   * `skipped` is `true` (create mode only) when nothing was added, and
   * `skipReason` explains why: `duplicate` = an identical opt-out already
   * exists; `covered_by_broader` = a broader (publication / global) opt-out
   * already covered the request.
   */
  onSubmitted: (result: {
    skipped?: boolean;
    skipReason?: OptOutSkipReason | null;
    updated?: boolean;
  }) => void;
};

const SCOPE_OPTIONS: SelectOption[] = [
  { value: "firm", label: "Firm-level — single law firm in a given publication" },
  { value: "edition", label: "Publication-wide — all firms in a given publication" },
  { value: "global", label: "Global — opt out from all Legal 500 emails" },
];

export function AddOptOutModal({ onClose, entry, onSubmitted }: AddOptOutModalProps) {
  const isEdit = Boolean(entry);
  const currentYear = new Date().getFullYear();
  const { toggleVisible, mode: pageMode } = useTaxonomyMode();
  const entryOnNewAxis = toggleVisible && isStoredEditionUuid(entry?.edition);
  const [mode, setMode] = useState<TaxonomyMode>(() => {
    if (!entry?.edition) return pageMode;
    return entryOnNewAxis ? "new" : "legacy";
  });
  const newAxis = mode === "new";

  const [email, setEmail] = useState(entry?.email ?? "");
  const [firstName, setFirstName] = useState(entry?.first_name ?? "");
  const [lastName, setLastName] = useState(entry?.last_name ?? "");
  const [scope, setScope] = useState<OptOutScope>(entry?.scope ?? "global");
  const [firmRef, setFirmRef] = useState<string | null>(entry?.firm_ref ?? null);
  const [publicationYear, setPublicationYear] = useState("");
  const [selectedPublicationId, setSelectedPublicationId] = useState(entry?.edition ?? "");
  const [legacyPublicationLabel, setLegacyPublicationLabel] = useState<string | null>(null);
  const [firmOptions, setFirmOptions] = useState<SelectOption[]>([]);
  const [firmsLoading, setFirmsLoading] = useState(false);
  const [firmsError, setFirmsError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const yearOptions = useMemo(() => buildPublicationYearOptions(currentYear), [currentYear]);

  const { data: publicationsData, loading: publicationsLoading } = usePublications(
    { edition: publicationYear || undefined },
    { enabled: !newAxis && !!publicationYear },
  );

  const { data: newAxisEditions, loading: newAxisEditionsLoading } = useNewAxisEditions({
    enabled: newAxis,
  });

  const selectedNewAxisEdition = useMemo(
    () =>
      newAxisEditions?.find(
        edition => edition.editionId.toLowerCase() === selectedPublicationId.toLowerCase(),
      ) ?? null,
    [newAxisEditions, selectedPublicationId],
  );

  const { data: lookupPublicationsData } = usePublications(
    {},
    { enabled: isEdit && isStoredLegacyPublicationId(entry?.edition) },
  );

  // Edit mode: resolve stored legacy_publication.id → year + publication picker.
  useEffect(() => {
    if (!entry?.edition) return;

    if (entryOnNewAxis) {
      const edition = newAxisEditions?.find(
        candidate => candidate.editionId.toLowerCase() === entry.edition?.toLowerCase(),
      );
      if (edition) {
        setSelectedPublicationId(edition.editionId.toLowerCase());
        setPublicationYear(extractPublicationYear(edition.editionName));
      }
      return;
    }

    if (isStoredLegacyPublicationId(entry.edition)) {
      setSelectedPublicationId(entry.edition);
      const pub = lookupPublicationsData?.publications.find(
        (p: LegacyPublication) => String(p.id) === entry.edition,
      );
      if (pub) {
        setPublicationYear(extractPublicationYear(pub.edition));
        setLegacyPublicationLabel(null);
      }
      return;
    }

    // Legacy rows stored a free-text edition string before publication-id migration.
    setSelectedPublicationId(entry.edition);
    setLegacyPublicationLabel(entry.edition);
  }, [entry, entryOnNewAxis, newAxisEditions, lookupPublicationsData?.publications]);

  const publicationOptions = useMemo(() => {
    if (newAxis) {
      const options = lowercaseEditionOptions(
        newAxisEditionOptions(newAxisEditions, publicationYear),
      );
      if (selectedNewAxisEdition && !options.some(o => o.value === selectedPublicationId)) {
        options.push({ value: selectedPublicationId, label: selectedNewAxisEdition.label });
      }
      return [{ value: "", label: "Select publication" }, ...options];
    }
    const pubs = publicationsData?.publications ?? [];
    const options = pubs.map((p: LegacyPublication) => formatLegacyPublicationOption(p));

    if (
      legacyPublicationLabel &&
      selectedPublicationId &&
      !options.some(o => o.value === selectedPublicationId)
    ) {
      options.push({ value: selectedPublicationId, label: legacyPublicationLabel });
    }

    if (
      isStoredLegacyPublicationId(selectedPublicationId) &&
      lookupPublicationsData?.publications &&
      !options.some(o => o.value === selectedPublicationId)
    ) {
      const pub = lookupPublicationsData.publications.find(
        (p: LegacyPublication) => String(p.id) === selectedPublicationId,
      );
      if (pub) options.push(formatLegacyPublicationOption(pub));
    }

    return [{ value: "", label: "Select publication" }, ...options];
  }, [
    newAxis,
    newAxisEditions,
    publicationYear,
    selectedNewAxisEdition,
    publicationsData?.publications,
    legacyPublicationLabel,
    selectedPublicationId,
    lookupPublicationsData?.publications,
  ]);

  const selectedPublicationIdNumber =
    !newAxis && isStoredLegacyPublicationId(selectedPublicationId)
      ? Number(selectedPublicationId)
      : null;
  const selectedEditionId =
    newAxis && isStoredEditionUuid(selectedPublicationId) ? selectedPublicationId : null;

  // Firms for the selected publication (firm scope only).
  useEffect(() => {
    if (scope !== "firm" || (!selectedPublicationIdNumber && !selectedEditionId)) {
      setFirmOptions([]);
      setFirmsLoading(false);
      setFirmsError(false);
      return;
    }
    let cancelled = false;
    setFirmsLoading(true);
    setFirmsError(false);
    submissionsService
      .getUniques(
        "firm",
        selectedEditionId
          ? { editionId: selectedEditionId, taxonomy: "new" }
          : { publicationId: selectedPublicationIdNumber ?? undefined },
      )
      .then(response => {
        if (cancelled) return;
        const seen = new Set<string>();
        const options: SelectOption[] = [];
        for (const firm of response.values) {
          const value = String(firm.id);
          if (!value || seen.has(value)) continue;
          seen.add(value);
          options.push({ value, label: firm.name });
        }
        setFirmOptions(options);
      })
      .catch(() => {
        if (!cancelled) setFirmsError(true);
      })
      .finally(() => {
        if (!cancelled) setFirmsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scope, selectedPublicationIdNumber, selectedEditionId]);

  const isValid = useMemo(() => {
    if (!email.trim()) return false;
    if (scope === "firm" && (!firmRef || !selectedPublicationId)) return false;
    if (scope === "edition" && !selectedPublicationId) return false;
    return true;
  }, [email, scope, firmRef, selectedPublicationId]);

  const firmSelectOptions = useMemo<SelectOption[]>(() => {
    const withPrefilled = (options: SelectOption[]): SelectOption[] => {
      if (!firmRef || options.some(o => o.value === firmRef)) return options;
      return [...options, { value: firmRef, label: firmRef }];
    };
    if (!selectedPublicationId)
      return withPrefilled([{ value: "", label: "Select a publication first" }]);
    if (firmsLoading) return withPrefilled([{ value: "", label: "Loading firms…" }]);
    if (firmsError) return withPrefilled([{ value: "", label: "Failed to load firms" }]);
    if (firmOptions.length === 0)
      return withPrefilled([{ value: "", label: "No firms found for this publication" }]);
    return withPrefilled([{ value: "", label: "Select firm" }, ...firmOptions]);
  }, [selectedPublicationId, firmsLoading, firmsError, firmOptions, firmRef]);

  const handleTaxonomyChange = (next: TaxonomyMode) => {
    setMode(next);
    setPublicationYear("");
    setSelectedPublicationId("");
    setLegacyPublicationLabel(null);
    setFirmRef(null);
  };

  const handleScopeChange = (nextScope: OptOutScope) => {
    setScope(nextScope);
    if (nextScope === "global") {
      setPublicationYear("");
      setSelectedPublicationId("");
      setLegacyPublicationLabel(null);
      setFirmRef(null);
    }
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);

    const payload: CreateOptOutInput = {
      email: email.trim(),
      scope,
      ...(firstName.trim() ? { first_name: firstName.trim() } : {}),
      ...(lastName.trim() ? { last_name: lastName.trim() } : {}),
      ...(scope === "firm" && firmRef ? { firm_ref: firmRef } : {}),
      ...((scope === "firm" || scope === "edition") && selectedPublicationId
        ? { edition: selectedPublicationId }
        : {}),
    };

    try {
      if (entry) {
        await optOuts.update(entry.id, payload);
        onSubmitted({ updated: true });
      } else {
        const result = await optOuts.create(payload);
        onSubmitted({ skipped: Boolean(result.skipped), skipReason: result.skip_reason ?? null });
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : `Failed to ${isEdit ? "update" : "create"} opt-out`,
      );
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? "Edit opt-out entry" : "Add opt-out entry"}
      size="medium"
      blurBackground
      backdropClick={false}
    >
      <div className={styles.form}>
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="referee@example.com"
          fullWidth
          required
        />

        <div className={styles.formRow}>
          <Input
            label="First name"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            fullWidth
          />
          <Input
            label="Last name"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            fullWidth
          />
        </div>

        <Select
          label="Scope"
          options={SCOPE_OPTIONS}
          value={scope}
          onChange={e => handleScopeChange(e.target.value as OptOutScope)}
          fullWidth
        />

        {(scope === "firm" || scope === "edition") && (
          <>
            {toggleVisible && (
              <TaxonomyToggle mode={mode} onChange={handleTaxonomyChange} disabled={submitting} />
            )}

            <Select
              label="Year"
              options={yearOptions}
              value={publicationYear}
              onChange={e => {
                setPublicationYear(e.target.value);
                setSelectedPublicationId("");
                setLegacyPublicationLabel(null);
                setFirmRef(null);
              }}
              fullWidth
            />

            <Select
              label="Publication"
              options={publicationOptions}
              value={selectedPublicationId}
              onChange={e => {
                setSelectedPublicationId(e.target.value);
                setLegacyPublicationLabel(null);
                setFirmRef(null);
              }}
              disabled={
                !publicationYear || (newAxis ? newAxisEditionsLoading : publicationsLoading)
              }
              fullWidth
            />
          </>
        )}

        {scope === "firm" && (
          <Select
            label="Firm"
            options={firmSelectOptions}
            value={firmRef ?? ""}
            onChange={e => setFirmRef(e.target.value || null)}
            disabled={!selectedPublicationId || firmsLoading}
            fullWidth
          />
        )}

        {error && (
          <div className={styles.formError}>
            <Icon icon="exclamation" size="small" />
            <span>{error}</span>
          </div>
        )}

        <div className={styles.modalFooter}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={submitting || !isValid}
          >
            {submitting ? (isEdit ? "Saving…" : "Adding…") : isEdit ? "Save" : "Add entry"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
